
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Chip,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import { Add, Search, Cancel } from '@mui/icons-material';
import _debounce from 'lodash/debounce';
import permissionService from '../../services/permissionService';

// Fonction utilitaire pour normaliser les IDs
const getPermissionId = (permission) => {
    if (!permission) return null;
    return permission._id || permission.id || permission.tempId;
};

const PermissionManager = ({
                               existingPermissions,
                               selectedPermissions,
                               onSelect,
                               onRemove,
                               onCreate
                           }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [newPermissionDialogOpen, setNewPermissionDialogOpen] = useState(false);
    const [newPermissionData, setNewPermissionData] = useState({
        name: '',
        description: '',
        category: 'custom'
    });
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const [validationError, setValidationError] = useState('');
    // Nouvel état pour suivre les permissions temporaires créées
    const [createdPermissions, setCreatedPermissions] = useState([]);

    // Log des props initiaux
    useEffect(() => {
        console.log('[PermissionManager] Initial props:', {
            existingPermissions,
            selectedPermissions
        });
    }, []);

    // Synchroniser les permissions créées avec les props
    useEffect(() => {
        // Récupérer les permissions temporaires des selectedPermissions
        const tempIds = selectedPermissions.filter(id =>
            typeof id === 'string' && id.startsWith('temp-')
        );

        // Vérifier si certaines ne sont pas dans createdPermissions
        const missingTempIds = tempIds.filter(id =>
            !createdPermissions.some(p => getPermissionId(p) === id)
        );

        // Si des permissions temporaires manquent, les récupérer depuis existingPermissions
        if (missingTempIds.length > 0) {
            const missingPermissions = existingPermissions.filter(p =>
                missingTempIds.includes(getPermissionId(p))
            );

            if (missingPermissions.length > 0) {
                setCreatedPermissions(prev => [...prev, ...missingPermissions]);
            }
        }
    }, [selectedPermissions, existingPermissions, createdPermissions]);

    // Recherche avec debounce et gestion des dépendances
    const debouncedSearch = useCallback(
        _debounce(async (query, currentPermissions, currentSelections) => {
            let response;
            try {
                if (query.length >= 2) {
                    console.log('[PermissionManager] Début recherche pour:', query);
                    setIsSearching(true);
                    setError(null);

                    response = await permissionService.searchPermissions({
                        query,
                        category: 'custom'
                    });

                    console.log('[PermissionManager] Réponse API brute:', response);
                    console.log('[PermissionManager] Type de réponse:', typeof response);

                    // Correction de l'extraction des résultats
                    const results = Array.isArray(response)
                        ? response
                        : Array.isArray(response?.data)
                            ? response.data
                            : response?.data?.permissions || [];

                    console.log('[PermissionManager] Résultats extraits:', results);

                    if (!Array.isArray(results)) {
                        throw new Error(`Format de réponse invalide (type: ${typeof results})`);
                    }

                    // Filtrage amélioré
                    const filtered = results.filter(r => {
                        const permissionId = getPermissionId(r);
                        const isSelected = currentSelections.some(selId =>
                            String(selId) === String(permissionId)
                        );
                        return !isSelected;
                    });

                    console.log('[PermissionManager] Résultats filtrés:', filtered);
                    setSearchResults(filtered);
                }
            } catch (err) {
                console.error('[PermissionManager] Erreur recherche:', {
                    error: err,
                    response,
                    query
                });
                setError(`Erreur lors de la recherche : ${err.message}`);
            } finally {
                setIsSearching(false);
            }
        }, 500),
        []
    );

    // Déclencheur de recherche
    useEffect(() => {
        console.log('[PermissionManager] Changement déclencheur recherche:', {
            searchQuery,
            existingPermissions: existingPermissions.length,
            selectedPermissions: selectedPermissions.length
        });
        debouncedSearch(searchQuery, existingPermissions, selectedPermissions);
    }, [searchQuery, debouncedSearch, existingPermissions, selectedPermissions]);

    // Nettoyage des résultats
    useEffect(() => {
        if (!searchQuery) {
            console.log('[PermissionManager] Réinitialisation résultats recherche');
            setSearchResults([]);
        }
    }, [searchQuery]);

    const handleCreateNewPermission = () => {
        console.log('[PermissionManager] Tentative création permission:', newPermissionData);

        // Validation du format
        const cleanedName = newPermissionData.name
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');

        console.log('[PermissionManager] Nom nettoyé:', cleanedName);

        if (!/^[a-z0-9_]{3,50}$/.test(cleanedName)) {
            console.warn('[PermissionManager] Validation échouée - format invalide');
            setValidationError('Format invalide (a-z, 0-9, _)');
            return;
        }

        // Vérification des doublons améliorée - INCLURE LES PERMISSIONS CRÉÉES
        const allPermissions = [
            ...existingPermissions,
            ...searchResults,
            ...createdPermissions
        ];

        console.log('[PermissionManager] Vérification doublons:', {
            cleanedName,
            permissionsCount: allPermissions.length
        });

        // Vérification plus robuste
        const duplicate = allPermissions.find(p => {
            if (!p || !p.name || typeof p.name !== 'string') return false;

            const existingName = p.name.toLowerCase();
            const newName = cleanedName.toLowerCase();
            const isDuplicate = existingName === newName;

            if (isDuplicate) {
                console.warn(`[PermissionManager] Doublon trouvé: "${existingName}" vs "${newName}"`);
            }

            return isDuplicate;
        });

        if (duplicate) {
            console.warn('[PermissionManager] Doublon détecté:', duplicate);
            setValidationError('Cette permission existe déjà');
            return;
        }

        // Création de la permission temporaire
        const tempId = `temp-${Date.now()}`;
        const tempPermission = {
            _id: tempId,
            id: tempId,
            name: cleanedName,
            description: newPermissionData.description,
            category: 'custom',
            isTemp: true
        };

        console.log('[PermissionManager] Création permission temporaire:', tempPermission);

        // AJOUTER À L'ÉTAT LOCAL AVANT D'APPELER ONCREATE
        setCreatedPermissions(prev => [...prev, tempPermission]);
        onCreate(tempPermission);

        setNewPermissionDialogOpen(false);
        setNewPermissionData({ name: '', description: '', category: 'custom' });
        setValidationError('');
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                Gestion des Permissions
            </Typography>

            {/* Champ de recherche */}
            <TextField
                fullWidth
                variant="outlined"
                label="Rechercher des permissions"
                value={searchQuery}
                onChange={(e) => {
                    console.log('[PermissionManager] Changement recherche:', e.target.value);
                    setSearchQuery(e.target.value.trimStart());
                }}
                InputProps={{
                    startAdornment: <Search sx={{ mr: 1 }} />,
                    endAdornment: isSearching && <CircularProgress size={20} />
                }}
                inputProps={{
                    'data-testid': 'permission-search-input'
                }}
            />

            {/* Résultats de recherche */}
            {searchQuery && (
                <List dense sx={{ maxHeight: 200, overflow: 'auto', mt: 2 }}>
                    {searchResults.map(permission => {
                        const permissionId = getPermissionId(permission);
                        console.log('[PermissionManager] Affichage résultat:', permissionId);

                        return (
                            <ListItem
                                key={permissionId}
                                dense
                                sx={{
                                    backgroundColor: 'background.paper',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    borderLeft: permission.isTemp ? '4px solid #ff9100' : 'none'
                                }}
                            >
                                <Checkbox
                                    checked={selectedPermissions.some(selId =>
                                        String(selId) === String(permissionId)
                                    )}
                                    onChange={() => {
                                        console.log('[PermissionManager] Changement sélection:', permissionId);
                                        const isSelected = selectedPermissions.some(selId =>
                                            String(selId) === String(permissionId)
                                        );

                                        isSelected
                                            ? onRemove(permissionId)
                                            : onSelect(permissionId);
                                    }}
                                    color="primary"
                                />
                                <ListItemText
                                    primary={permission.name}
                                    secondary={
                                        <Typography variant="body2" color="textSecondary">
                                            {permission.description || 'Aucune description'}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            )}

            {/* Gestion des erreurs */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Bouton de création */}
            <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => {
                    console.log('[PermissionManager] Ouverture dialogue création');
                    setNewPermissionDialogOpen(true);
                }}
                sx={{
                    mt: 2,
                    borderStyle: 'dashed',
                    '&:hover': { borderStyle: 'dashed' }
                }}
            >
                Créer une nouvelle permission
            </Button>

            {/* Permissions sélectionnées */}
            <Box sx={{
                mt: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                minHeight: 40,
                alignItems: 'center'
            }}>
                {selectedPermissions.map(permId => {
                    const permission = [
                        ...existingPermissions,
                        ...searchResults,
                        ...createdPermissions // INCLURE LES PERMISSIONS CRÉÉES
                    ].find(p =>
                        String(getPermissionId(p)) === String(permId)
                    ) || {
                        _id: permId,
                        id: permId,
                        name: 'Nouvelle permission',
                        isTemp: String(permId).startsWith('temp-')
                    };

                    console.log('[PermissionManager] Affichage permission sélectionnée:', permission);

                    return (
                        <Tooltip
                            key={permId}
                            title={permission.description || 'Aucune description'}
                            arrow
                        >
                            <Chip
                                label={permission.name}
                                onDelete={() => {
                                    console.log('[PermissionManager] Suppression permission:', permId);
                                    onRemove(permId);

                                    // SUPPRIMER DE L'ÉTAT LOCAL SI C'EST UNE PERMISSION TEMPORAIRE
                                    if (String(permId).startsWith('temp-')) {
                                        setCreatedPermissions(prev =>
                                            prev.filter(p => getPermissionId(p) !== permId)
                                        );
                                    }
                                }}
                                color={String(permId).startsWith('temp-') ? 'secondary' : 'default'}
                                deleteIcon={<Cancel />}
                                sx={{
                                    maxWidth: 200,
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    },
                                    backgroundColor: String(permId).startsWith('temp-') ? '#fff3e0' : 'default'
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>

            {/* Dialogue de création */}
            <Dialog
                open={newPermissionDialogOpen}
                onClose={() => {
                    console.log('[PermissionManager] Fermeture dialogue création');
                    setNewPermissionDialogOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Créer une nouvelle permission</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Nom technique"
                        value={newPermissionData.name}
                        onChange={(e) => {
                            console.log('[PermissionManager] Changement nom permission:', e.target.value);
                            setNewPermissionData({
                                ...newPermissionData,
                                name: e.target.value
                            });
                        }}
                        margin="normal"
                        error={!!validationError}
                        helperText={validationError || "Exemple: gestion_utilisateurs"}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={newPermissionData.description}
                        onChange={(e) => {
                            console.log('[PermissionManager] Changement description permission:', e.target.value);
                            setNewPermissionData({
                                ...newPermissionData,
                                description: e.target.value
                            });
                        }}
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            console.log('[PermissionManager] Annulation création permission');
                            setNewPermissionDialogOpen(false);
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleCreateNewPermission}
                        color="primary"
                        variant="contained"
                    >
                        Créer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PermissionManager;